import { readonly, ref, type Ref } from 'vue';

/**
 * Cookie and storage consent, shared by the landing and the game. Necessary
 * storage is always on; everything else waits for an explicit choice. Code
 * that wants to set a non-essential cookie, load an analytics or marketing
 * script, or store a preference asks `allowed(cat)` first, or registers
 * `whenAllowed(cat, fn)` so it starts the moment the visitor agrees (and
 * never before).
 *
 * The record lives in localStorage under `admiralty.consent.v1` (necessary: it
 * is the record of the choice itself). It carries the cookie-policy version it
 * was given for; a newer `COOKIE_POLICY_VERSION` asks again.
 *
 * Ported from Tankodromo (engine/consent/consent.ts); see
 * design/quick-specs/cookie-consent.md.
 */
export const CONSENT_CATEGORIES = ['necessary', 'preferences', 'analytics', 'marketing'] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];
export type OptionalCategory = Exclude<ConsentCategory, 'necessary'>;
export const OPTIONAL_CATEGORIES: readonly OptionalCategory[] = ['preferences', 'analytics', 'marketing'];

/** Bump when the cookie list or its purposes change: every visitor is asked again. */
export const COOKIE_POLICY_VERSION = '2026-10-14';
export const CONSENT_STORAGE_KEY = 'admiralty.consent.v1';

export interface ConsentRecord {
  version: string;
  /** ISO time of the choice. */
  at: string;
  /** How it was given (for the audit trail); `withdraw` = everything optional turned off after a yes. */
  method: 'accept_all' | 'reject_all' | 'custom' | 'withdraw';
  choices: Record<OptionalCategory, boolean>;
  /** The server's id for this browser's decisions (links later changes to earlier ones). */
  consentId?: string;
}

/**
 * Where each decision is reported for the server-side audit trail
 * (`POST /api/consent`). The local record is the source of truth; a failed
 * report never blocks the visitor.
 */
export const consentConfig: { endpoint: string | null } = { endpoint: `${import.meta.env.VITE_API_URL || ''}/api/consent` };

function load(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as Partial<ConsentRecord>;
    if (r.version !== COOKIE_POLICY_VERSION || !r.choices || typeof r.at !== 'string') return null;
    return {
      version: r.version,
      at: r.at,
      method: r.method === 'accept_all' || r.method === 'reject_all' || r.method === 'withdraw' ? r.method : 'custom',
      choices: { preferences: r.choices.preferences === true, analytics: r.choices.analytics === true, marketing: r.choices.marketing === true },
      ...(typeof r.consentId === 'string' ? { consentId: r.consentId } : {}),
    };
  } catch {
    return null;
  }
}

const record = ref<ConsentRecord | null>(load());
/** Whether the preferences dialog is open (the footer's and the settings sheet's "Cookie settings" open it). */
const settingsOpen = ref(false);
const waiting: Array<{ cat: OptionalCategory; fn: () => void }> = [];
const listeners: Array<(rec: ConsentRecord) => void> = [];

/** The id the server gave this browser, kept across policy versions. */
function storedConsentId(): string | undefined {
  try {
    const r = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) ?? '{}') as { consentId?: unknown };
    return typeof r.consentId === 'string' ? r.consentId : undefined;
  } catch {
    return undefined;
  }
}

function store(rec: ConsentRecord): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(rec));
  } catch {
    /* storage blocked: the choice holds for this visit */
  }
}

export const consent = {
  /** The stored choice for the current policy version, or null (banner shows). */
  record: readonly(record) as Readonly<Ref<ConsentRecord | null>>,
  settingsOpen,
  allowed(cat: ConsentCategory): boolean {
    if (cat === 'necessary') return true;
    return record.value?.choices[cat] === true;
  },
  /** Runs `fn` once `cat` is allowed: now if it already is, else after the visitor agrees. */
  whenAllowed(cat: OptionalCategory, fn: () => void): void {
    if (consent.allowed(cat)) fn();
    else waiting.push({ cat, fn });
  },
  save(choices: Record<OptionalCategory, boolean>, method: ConsentRecord['method']): void {
    const prev = record.value;
    const none = !choices.preferences && !choices.analytics && !choices.marketing;
    const hadYes = !!prev && (prev.choices.preferences || prev.choices.analytics || prev.choices.marketing);
    const consentId = prev?.consentId ?? storedConsentId();
    const rec: ConsentRecord = {
      version: COOKIE_POLICY_VERSION,
      at: new Date().toISOString(),
      method: none && hadYes ? 'withdraw' : method,
      choices: { ...choices },
      ...(consentId ? { consentId } : {}),
    };
    record.value = rec;
    settingsOpen.value = false;
    store(rec);
    for (const l of listeners) l(rec);
    for (let i = waiting.length - 1; i >= 0; i--) {
      const w = waiting[i]!;
      if (rec.choices[w.cat]) {
        waiting.splice(i, 1);
        w.fn();
      }
    }
    report(rec);
  },
  acceptAll(): void {
    consent.save({ preferences: true, analytics: true, marketing: true }, 'accept_all');
  },
  rejectAll(): void {
    consent.save({ preferences: false, analytics: false, marketing: false }, 'reject_all');
  },
  /** Called after every decision (pref-storage flushes or clears settings here). */
  onChange(fn: (rec: ConsentRecord) => void): void {
    listeners.push(fn);
  },
  openSettings(): void {
    settingsOpen.value = true;
  },
  /** Re-reads storage (tests, or another tab). */
  reload(): void {
    record.value = load();
  },
};

function report(rec: ConsentRecord): void {
  const url = consentConfig.endpoint;
  if (!url || typeof fetch === 'undefined') return;
  void fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rec),
    keepalive: true,
  })
    .then(async (r) => {
      if (!r.ok) return;
      const body = (await r.json().catch(() => null)) as { consentId?: unknown } | null;
      const id = body?.consentId;
      // Keep the server id with the record so the next change links to this one.
      if (typeof id === 'string' && record.value && record.value.at === rec.at) {
        record.value = { ...record.value, consentId: id };
        store(record.value);
      }
    })
    .catch(() => undefined);
}
